import {
  useQuery,
  QueryKey,
  QueryObserverOptions,
  UseQueryResult,
  useMutation,
  MutationKey,
  MutateOptions,
} from "react-query";

import {
  roleListService,
  roleDetailService,
  roleCreateService,
  roleUpdateService,
  roleListShortService,
  roleUpdateStatusService,
  roleDeleteService,
} from "./services";

type RoleListResponse = Awaited<ReturnType<typeof roleListService>>;
type RoleListShortResponse = Awaited<ReturnType<typeof roleListShortService>>;
type RoleDetailResponse = Awaited<ReturnType<typeof roleDetailService>>;

export const useRoles = ({
  queryKey,
  params,
  options,
}: {
  queryKey?: QueryKey | string;
  params: Parameters<typeof roleListService>[0];
  options?: QueryObserverOptions<unknown, unknown, RoleListResponse>;
}) => {
  const response = useQuery({
    queryKey: queryKey ?? "roleHooks",
    queryFn: () => roleListService(params),
    ...options,
  });

  return response as UseQueryResult<RoleListResponse>;
};

export const useShortRoles = ({
  queryKey,
  params,
  options,
}: {
  queryKey?: QueryKey | string;
  params: Parameters<typeof roleListShortService>[0];
  options?: QueryObserverOptions<unknown, unknown, RoleListShortResponse>;
}) => {
  const response = useQuery({
    queryKey: queryKey ?? "roleShortHooks",
    queryFn: () => roleListShortService(params),
    ...options,
  });

  return response as UseQueryResult<RoleListShortResponse>;
};

export const useRoleDetail = ({
  queryKey,
  params,
  options,
}: {
  queryKey?: QueryKey | string;
  params: Parameters<typeof roleDetailService>[0];
  options?: QueryObserverOptions<unknown, unknown, RoleDetailResponse>;
}) => {
  const response = useQuery({
    queryKey: queryKey ?? "roleDetailHooks",
    queryFn: () => roleDetailService(params),
    ...options,
  });

  return response as UseQueryResult<RoleDetailResponse>;
};

export const useRoleCreate = ({
  queryKey,
  options,
}: {
  queryKey?: MutationKey | string;
  options?: MutateOptions<
    RoleDetailResponse,
    unknown,
    Parameters<typeof roleCreateService>[0]
  >;
}) => {
  const response = useMutation({
    mutationKey: queryKey ?? "roleCreateService",
    mutationFn: roleCreateService,
    onSuccess: options?.onSuccess as unknown as () => void,
    onError: options?.onError as unknown as () => void,
  });

  return response;
};
export const useRoleUpdate = ({
  queryKey,
  options,
}: {
  queryKey?: MutationKey | string;
  options?: MutateOptions<
    RoleDetailResponse,
    unknown,
    Parameters<typeof roleUpdateService>[0]
  >;
}) => {
  const response = useMutation({
    mutationKey: queryKey ?? "tenantUpdateHooks",
    mutationFn: roleUpdateService,
    onSuccess: options?.onSuccess as unknown as () => void,
    onError: options?.onError as unknown as () => void,
  });

  return response;
};

export const useRoleUpdateStatus = ({
  queryKey,
  options,
}: {
  queryKey?: MutationKey | string;
  options?: MutateOptions<
    RoleDetailResponse,
    unknown,
    Parameters<typeof roleUpdateStatusService>[0]
  >;
}) => {
  const response = useMutation({
    mutationKey: queryKey ?? "roleUpdateStatusService",
    mutationFn: roleUpdateStatusService,
    onSuccess: options?.onSuccess as unknown as () => void,
    onError: options?.onError as unknown as () => void,
  });

  return response;
};

export const useRoleDelete = ({
  queryKey,
  options,
}: {
  queryKey?: MutationKey | string;
  options?: MutateOptions<
    RoleDetailResponse,
    unknown,
    Parameters<typeof roleDeleteService>[0]
  >;
}) => {
  const response = useMutation({
    mutationKey: queryKey ?? "roleDeleteService",
    mutationFn: roleDeleteService,
    onSuccess: options?.onSuccess as unknown as () => void,
    onError: options?.onError as unknown as () => void,
  });

  return response;
};
